import { useState } from "react";
import {
  Abas,
  Alerta,
  Bloco,
  BotaoDocumento,
  HeroModulo,
  Campo,
  CAMPO_SIM,
  Linha,
  Referencias,
  Tabela,
  abrirDocumento,
} from "../componentes/HubDpUi";
import { reais, type ParametrosFolha } from "../lib/folhaDp";
import {
  FASES,
  IDS_VALIDOS,
  MODALIDADES,
  TOTAL_ITENS,
  prazosDaAdmissao,
  progresso,
  type Modalidade,
} from "../lib/admissaoDp";
import { contratoTrabalho } from "../lib/documentosDp";
import { mascararMoeda, mascararInteiro, mascararCnpj, mascararCpf, mascararData } from "../lib/mascaras";
import { REFERENCIA_ADMISSAO } from "../componentes/HubDpTextos";
import { reaisPorExtenso } from "../lib/extenso";

// O módulo Admissão: o roteiro em três fases e o contrato nas cinco modalidades.
//
// O checklist guarda o progresso em `localStorage` — é a única coisa do HUB DP
// que sobrevive a recarregar a página, e é deliberado: admissão leva dias, e
// perder as marcas a cada visita tornaria a aba inútil. Não é dado do aluno no
// banco: fica no navegador dele, e a ferramenta segue sem tabela de estado.

const BASE = "/ferramenta/hub-dp/admissao";
const CHAVE = "cf:hub-dp:admissao:marcados";
const ABAS = [
  { slug: "", rotulo: "Roteiro" },
  { slug: "contrato", rotulo: "Contrato" },
  { slug: "prazos", rotulo: "Prazos" },
  { slug: "referencia", rotulo: "Referência" },
];

function lerMarcados(): Record<string, boolean> {
  try {
    const cru = JSON.parse(localStorage.getItem(CHAVE) ?? "{}");
    if (typeof cru !== "object" || cru === null) return {};
    // Só ids que EXISTEM entram. Um id antigo, de quando um item tinha outro
    // nome, contaria como concluído para sempre — e o aluno veria "19 de 19" com
    // item nunca marcado. É `progresso()` que faz o filtro no cálculo; aqui o
    // filtro é para não guardar lixo de volta.
    return Object.fromEntries(
      Object.entries(cru as Record<string, unknown>)
        .filter(([id, v]) => IDS_VALIDOS.has(id) && v === true)
        .map(([id]) => [id, true]),
    );
  } catch {
    // Armazenamento bloqueado (janela privada, site data desligado) não pode
    // derrubar a tela: o roteiro continua servindo sem memória.
    return {};
  }
}

interface FormContrato {
  empresa: string;
  cnpj: string;
  enderecoEmpresa: string;
  empregado: string;
  cpf: string;
  enderecoEmpregado: string;
  cargo: string;
  salario: string;
  jornada: string;
  local: string;
  inicio: string;
  modalidade: Modalidade;
  diasExperiencia: string;
  cidade: string;
  cursoAprendizagem: string;
}

const VAZIO: FormContrato = {
  empresa: "",
  cnpj: "",
  enderecoEmpresa: "",
  empregado: "",
  cpf: "",
  enderecoEmpregado: "",
  cargo: "",
  salario: "",
  jornada: "44 horas semanais, de segunda a sexta",
  local: "sede da empresa",
  inicio: "",
  modalidade: "experiencia",
  diasExperiencia: "45",
  cidade: "",
  cursoAprendizagem: "",
};

const dataDe = (texto: string): Date | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto.trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return Number.isNaN(d.getTime()) ? null : d;
};
const br = (d: Date) => d.toLocaleDateString("pt-BR");

export default function DpAdmissao({ p, aba }: { p: ParametrosFolha; aba: string }) {
  const [marcados, setMarcados] = useState<Record<string, boolean>>(lerMarcados);
  const [f, setF] = useState<FormContrato>(VAZIO);
  const [bloqueado, setBloqueado] = useState(false);
  const troca = <K extends keyof FormContrato>(c: K, v: FormContrato[K]) =>
    setF((a) => ({ ...a, [c]: v }));

  const pr = progresso(marcados);

  const alternar = (id: string) => {
    const novo = { ...marcados, [id]: !marcados[id] };
    if (!novo[id]) delete novo[id];
    setMarcados(novo);
    try {
      localStorage.setItem(CHAVE, JSON.stringify(novo));
    } catch {
      /* sem memória, a tela segue funcionando */
    }
  };

  const inicio = dataDe(f.inicio);
  const prazos = inicio ? prazosDaAdmissao(inicio, Number(f.diasExperiencia.replace(/\D+/g, "")) || 0) : null;

  return (
    <>
      <HeroModulo
        titulo="Admissão"
        subtitulo="O roteiro em três fases e o contrato de trabalho nas cinco modalidades."
        heroi={{
          rotulo: "Roteiro concluído",
          valor: `${pr.concluidos} de ${pr.total}`,
          detalhe:
            pr.criticosPendentes.length > 0
              ? `${pr.criticosPendentes.length} item(ns) crítico(s) pendente(s)`
              : "nenhum item crítico pendente",
        }}
      />
      <Abas base={BASE} abas={ABAS} />

      {aba === "" && (
        <div className="space-y-6">
          <div id="resultado" className="surface flex flex-wrap items-center justify-between gap-4 p-5">
            <div>
              <p className="font-display text-2xl font-bold text-titulo tabular-nums">
                {pr.concluidos} de {pr.total}
              </p>
              <p className="text-xs text-cinza">itens do roteiro concluídos ({pr.percentual}%)</p>
            </div>
            <div className="min-w-[180px] grow">
              <div
                className="h-2.5 overflow-hidden rounded-full bg-superficie-2"
                role="progressbar"
                aria-valuenow={pr.percentual}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Progresso do roteiro de admissão"
              >
                <div className="h-full rounded-full bg-laranja" style={{ width: `${pr.percentual}%` }} />
              </div>
            </div>
            {pr.concluidos > 0 && (
              <button
                type="button"
                className="rounded-full border border-borda px-3 py-1.5 text-xs font-semibold text-titulo"
                onClick={() => {
                  setMarcados({});
                  try {
                    localStorage.removeItem(CHAVE);
                  } catch {
                    /* nada a limpar */
                  }
                }}
              >
                Recomeçar
              </button>
            )}
          </div>

          {pr.criticosPendentes.length > 0 && (
            <Alerta tom="erro" titulo={`${pr.criticosPendentes.length} item(ns) crítico(s) pendente(s)`}>
              Estes geram multa ou nulidade se ficarem para depois:{" "}
              {pr.criticosPendentes.map((i) => i.titulo).join(" · ")}.
            </Alerta>
          )}

          {FASES.map((fase) => (
            <section key={fase.id} className="space-y-3">
              <div>
                <h2 className="font-display text-lg font-bold text-titulo">{fase.titulo}</h2>
                <p className="text-xs text-cinza">{fase.quando}</p>
              </div>
              <ul className="space-y-2">
                {fase.itens.map((item) => (
                  <li
                    key={item.id}
                    className={
                      "rounded-cf border bg-superficie p-4 " +
                      (item.critico ? "border-l-4 border-l-erro-tinta border-borda" : "border-borda")
                    }
                  >
                    <label className="flex gap-3">
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 shrink-0 accent-laranja"
                        checked={Boolean(marcados[item.id])}
                        onChange={() => alternar(item.id)}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-tinta">
                          {item.titulo}
                          {item.critico && (
                            <span className="ml-2 rounded bg-erro-fundo px-1.5 py-0.5 align-middle text-[10px] font-bold text-erro-tinta uppercase">
                              crítico
                            </span>
                          )}
                        </span>
                        <span className="mt-1 block text-xs leading-relaxed text-cinza">{item.detalhe}</span>
                        {(item.prazo || item.base) && (
                          <span className="mt-1.5 block text-[11px] text-cinza">
                            {item.prazo && <strong className="text-tinta">{item.prazo}</strong>}
                            {item.prazo && item.base && " · "}
                            {item.base}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <p className="text-xs leading-relaxed text-cinza">
            As marcas ficam guardadas no seu navegador — admissão leva dias, e perdê-las a cada
            visita tornaria o roteiro inútil. Nada é enviado ao servidor.
          </p>
        </div>
      )}

      {aba === "contrato" && (
        <div className="space-y-6">
          <form id="dados-do-contrato" className="surface grid gap-4 p-5 sm:grid-cols-2 sm:p-6" onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Modalidade" largo>
              <select
                className={CAMPO_SIM}
                value={f.modalidade}
                onChange={(e) => troca("modalidade", e.target.value as Modalidade)}
              >
                {(Object.keys(MODALIDADES) as Modalidade[]).map((m) => (
                  <option key={m} value={m}>
                    {MODALIDADES[m].rotulo}
                  </option>
                ))}
              </select>
            </Campo>

            <div className="sm:col-span-2">
              <Alerta tom="atencao" titulo={MODALIDADES[f.modalidade].rotulo}>
                {MODALIDADES[f.modalidade].resumo} <strong>{MODALIDADES[f.modalidade].atencao}</strong>{" "}
                <span className="text-xs">({MODALIDADES[f.modalidade].base})</span>
              </Alerta>
            </div>

            <Campo rotulo="Empresa">
              <input className={CAMPO_SIM} value={f.empresa} onChange={(e) => troca("empresa", e.target.value)} />
            </Campo>
            <Campo rotulo="CNPJ">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                value={f.cnpj}
                onChange={(e) => troca("cnpj", mascararCnpj(e.target.value))}
              />
            </Campo>
            <Campo rotulo="Endereço da empresa" largo>
              <input
                className={CAMPO_SIM}
                value={f.enderecoEmpresa}
                onChange={(e) => troca("enderecoEmpresa", e.target.value)}
              />
            </Campo>
            <Campo rotulo="Empregado">
              <input className={CAMPO_SIM} value={f.empregado} onChange={(e) => troca("empregado", e.target.value)} />
            </Campo>
            <Campo rotulo="CPF">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                value={f.cpf}
                onChange={(e) => troca("cpf", mascararCpf(e.target.value))}
              />
            </Campo>
            <Campo rotulo="Endereço do empregado" largo>
              <input
                className={CAMPO_SIM}
                value={f.enderecoEmpregado}
                onChange={(e) => troca("enderecoEmpregado", e.target.value)}
              />
            </Campo>
            <Campo rotulo="Cargo">
              <input className={CAMPO_SIM} value={f.cargo} onChange={(e) => troca("cargo", e.target.value)} />
            </Campo>
            <Campo rotulo="Salário mensal">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.salario}
                onChange={(e) => troca("salario", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
            <Campo rotulo="Jornada">
              <input className={CAMPO_SIM} value={f.jornada} onChange={(e) => troca("jornada", e.target.value)} />
            </Campo>
            <Campo rotulo="Local de trabalho">
              <input className={CAMPO_SIM} value={f.local} onChange={(e) => troca("local", e.target.value)} />
            </Campo>
            <Campo rotulo="Início das atividades">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="dd/mm/aaaa"
                value={f.inicio}
                onChange={(e) => troca("inicio", mascararData(e.target.value))}
              />
            </Campo>
            <Campo rotulo="Cidade do foro">
              <input className={CAMPO_SIM} value={f.cidade} onChange={(e) => troca("cidade", e.target.value)} />
            </Campo>
            {f.modalidade === "experiencia" && (
              <Campo rotulo="Dias de experiência" ajuda="O primeiro período. O total, somando a prorrogação, não pode passar de 90.">
                <input
                  className={CAMPO_SIM}
                  inputMode="numeric"
                  value={f.diasExperiencia}
                  onChange={(e) => troca("diasExperiencia", mascararInteiro(e.target.value, 3))}
                />
              </Campo>
            )}
            {f.modalidade === "aprendiz" && (
              <Campo rotulo="Curso de aprendizagem" largo>
                <input
                  className={CAMPO_SIM}
                  placeholder="Aprendizagem em Administração — SENAC"
                  value={f.cursoAprendizagem}
                  onChange={(e) => troca("cursoAprendizagem", e.target.value)}
                />
              </Campo>
            )}
          </form>

          {f.modalidade === "experiencia" && prazos?.semEspacoParaProrrogar && (
            <Alerta tom="erro" titulo="Não cabe prorrogar">
              O período informado já usa os 90 dias que a lei permite no total. Prorrogar converte o
              contrato em prazo <strong>indeterminado</strong> — e a rescisão passa a custar aviso
              prévio e multa de 40% do FGTS.
            </Alerta>
          )}
          {f.modalidade === "experiencia" && prazos?.excedeLimiteLegal && (
            <Alerta tom="erro" titulo="O período excede o limite legal">
              A experiência não pode passar de 90 dias (CLT art. 445, parágrafo único). Acima disso o
              contrato já nasce por prazo indeterminado.
            </Alerta>
          )}

          <BotaoDocumento
            rotulo="Gerar o contrato"
            bloqueado={bloqueado}
            aoClicar={() => {
              const valor = mascararMoeda(f.salario).numero ?? 0;
              const ok = abrirDocumento(
                contratoTrabalho({
                  empresa: f.empresa,
                  cnpj: f.cnpj,
                  enderecoEmpresa: f.enderecoEmpresa,
                  empregado: f.empregado,
                  cpf: f.cpf,
                  enderecoEmpregado: f.enderecoEmpregado,
                  cargo: f.cargo,
                  salario: valor > 0 ? reais(valor) : "",
                  // O valor por extenso vem do módulo puro que o contrato do IR
                  // Proposta já usa — uma definição só de "três mil reais".
                  salarioExtenso: valor > 0 ? reaisPorExtenso(valor) : "",
                  jornada: f.jornada,
                  local: f.local,
                  inicio: f.inicio,
                  modalidade: f.modalidade,
                  fimPrazo: prazos?.fimExperiencia ? br(prazos.fimExperiencia) : "",
                  diasExperiencia: Number(f.diasExperiencia.replace(/\D+/g, "")) || 0,
                  cidade: f.cidade,
                  dataEmissao: new Date().toLocaleDateString("pt-BR"),
                  cursoAprendizagem: f.cursoAprendizagem,
                }),
              );
              setBloqueado(!ok);
            }}
          />
          <p className="text-xs leading-relaxed text-cinza">
            O contrato abre em outra aba, em nove cláusulas, pronto para imprimir em duas vias. A
            cláusula 2ª muda com a modalidade; as outras oito são iguais em todas.
          </p>
        </div>
      )}

      {aba === "prazos" && (
        <div className="space-y-6">
          <form id="dados-dos-prazos" className="surface grid gap-4 p-5 sm:grid-cols-2 sm:p-6" onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Início das atividades">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="dd/mm/aaaa"
                value={f.inicio}
                onChange={(e) => troca("inicio", mascararData(e.target.value))}
              />
            </Campo>
            <Campo rotulo="Dias de experiência" ajuda="Zero se não houver contrato de experiência.">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                value={f.diasExperiencia}
                onChange={(e) => troca("diasExperiencia", mascararInteiro(e.target.value, 3))}
              />
            </Campo>
          </form>

          {!prazos ? (
            <Alerta tom="atencao">
              Informe o início das atividades em dd/mm/aaaa para ver os prazos.
            </Alerta>
          ) : (
            <div id="resultado" className="space-y-4">
              <Bloco titulo="Os prazos desta admissão" destaque>
                <Linha
                  rotulo="S-2200 no eSocial"
                  valor={br(prazos.limiteS2200)}
                  ajuda="até o dia ANTERIOR ao início — transmitir depois é registro em atraso (CLT art. 47)"
                />
                <Linha rotulo="Início das atividades" valor={br(prazos.inicio)} />
                {prazos.fimExperiencia && (
                  <Linha
                    rotulo="Fim da experiência"
                    valor={br(prazos.fimExperiencia)}
                    ajuda="o primeiro dia CONTA no período"
                  />
                )}
                {prazos.fimExperienciaProrrogada && (
                  <Linha
                    rotulo="Limite com a prorrogação"
                    valor={br(prazos.fimExperienciaProrrogada)}
                    ajuda={`sobram ${prazos.diasDisponiveisProrrogacao} dias dentro dos 90`}
                  />
                )}
                {/*
                  Quando NÃO cabe prorrogar, a linha acima desaparece — e a
                  ausência não informa nada. A prova de tela pegou isto: com 90
                  dias diretos a aba não dizia que a prorrogação está esgotada,
                  que é justamente o que o aluno precisa saber. Linha explícita
                  no lugar do silêncio.
                */}
                {prazos.semEspacoParaProrrogar && (
                  <Linha
                    rotulo="Prorrogação"
                    valor="não cabe"
                    ajuda="o período informado já usa os 90 dias que a lei permite no total"
                  />
                )}
              </Bloco>

              <Alerta tom={prazos.semEspacoParaProrrogar || prazos.excedeLimiteLegal ? "erro" : "informacao"} titulo="Os 90 dias são do TOTAL">
                Não são 90 + 90. O período inicial mais a prorrogação — uma só — têm de caber em 90
                dias. Passar disso converte o contrato em prazo indeterminado, e a empresa descobre
                na rescisão, quando aparecem o aviso prévio e a multa de 40%.
              </Alerta>
            </div>
          )}
        </div>
      )}

      {aba === "referencia" && (
        <div className="space-y-6">
          {REFERENCIA_ADMISSAO.map((t) => (
            <Tabela key={t.titulo} tabela={t} />
          ))}
          <Tabela
            tabela={{
              titulo: "As cinco modalidades da CLT",
              colunas: ["Modalidade", "Prazo máximo", "Prorrogações", "Base legal"],
              linhas: (Object.keys(MODALIDADES) as Modalidade[]).map((m) => [
                MODALIDADES[m].rotulo,
                MODALIDADES[m].duracaoMaximaDias === null
                  ? "sem prazo"
                  : `${MODALIDADES[m].duracaoMaximaDias} dias`,
                String(MODALIDADES[m].prorrogacoes),
                MODALIDADES[m].base,
              ]),
            }}
          />
          <Alerta tom="erro" titulo="O S-2200 antes do primeiro dia">
            É o item mais crítico do roteiro. O prazo é até o dia imediatamente anterior ao início
            das atividades; depois disso o registro é em atraso, com multa por empregado que sobe se
            houver fiscalização.
          </Alerta>
          <Alerta tom="atencao" titulo="O ASO antes de tudo">
            O exame admissional tem de acontecer antes do início (NR-7). Admitir sem ASO é infração
            autônoma e responsabiliza a empresa se houver acidente no primeiro dia.
          </Alerta>
          <p className="text-xs leading-relaxed text-cinza">
            O roteiro tem {TOTAL_ITENS} itens em 3 fases. Os marcados como <strong>críticos</strong>{" "}
            são os que geram multa ou nulidade se ficarem para depois.
          </p>
        </div>
      )}

      <Referencias inss={p.referenciaInss} irrf={p.referenciaIrrf} />
    </>
  );
}
